/* eslint-disable max-lines-per-function */
import React, { PropsWithChildren, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { isNil } from "lodash";
import { ElasticFilterInput, FilterCondition } from "types/gql-types";

type RangeData = string | number;
type ArrayFilterSetting = (string | number | boolean)[];
export type RangeFilterSetting = { min?: RangeData; max?: RangeData };
type FilterSetting = string | number | boolean | RangeFilterSetting | ArrayFilterSetting;
type FilterType = "text" | "number" | "boolean" | "range" | "rangeMin" | "rangeMax" | "array" | "inverseBoolean";

export type FilterData = {
  type: "text" | "number" | "boolean" | "range" | "array" | "inverseBoolean";
  value?: FilterSetting;
  values?: ArrayFilterSetting;
  min?: RangeData;
  max?: RangeData;
}

type FilterRecordType = Record<string, FilterData>

type PageState = {
  page: number;
  nbPages: number;
  pageSize: number;
};

type SearchState = {
  page: number;
  nbHits: number;
  hits: any[];
};
type FilterManager = {
  filterCondition?: FilterCondition;
  setFilterCondition: (condition?: FilterCondition, resetPage?: boolean) => void;
  setTextFilter: (query: string) => void;
  addFilter: (filterName: string, value: FilterSetting, filterType?: FilterType) => void;
  removeFilter: (filterName: string, filterType?: FilterType) => void;
  clearFilters: () => void;
  pageState: PageState;
  setNbPages: (nbPages: number) => void;
  setSelectedPage: (page: number) => void;
  filterState: FilterRecordType;
  setFilterState: React.Dispatch<React.SetStateAction<FilterRecordType>>;
  searchState: SearchState,
  setSearchState: React.Dispatch<React.SetStateAction<SearchState>>;
  search: () => void;
  interestListIds: string[];
  setInterestListIds: (ids: string[]) => void;
  textFilter: string;
  searchFlag: boolean;
  setSearchFlag: React.Dispatch<React.SetStateAction<boolean>>;
}

const SidebarFiltersContext = React.createContext<FilterManager | null>(null);

export const formatFilters = (input: FilterRecordType): ElasticFilterInput[] =>
  Object.entries(input).map(([field, { type, value }]) => {
    const output = { field, type } as ElasticFilterInput;
    if (type === "array") {
      output.values = value as string[];
    } else if (type === "range" && value) {
      const rangeFilter = value as RangeFilterSetting;
      output.min = isNil(rangeFilter.min) || rangeFilter.min === "" ? null : `${rangeFilter.min}`;
      output.max = isNil(rangeFilter.max) || rangeFilter.max === "" ? null : `${rangeFilter.max}`;
    } else {
      output.value = isNil(value) ? null : `${value}`;
    }
    return output;
  });

const listPagePath = (pathname: string) => {
  const match = pathname.match(/^(\/dash\/manager\/lists\/[^/]+)(?:\/p\/[^/]+)?(\/.*)?$/);
  if (!match) return null;
  return `${match[1]}/p/1${match[2] ?? ""}`;
};

const defineFilters = (): FilterManager => {
  const navigate = useNavigate();
  const [filterCondition, updateFilterCondition] = useState<FilterCondition>();
  const [interestListIds, setInterestListIds] = useState<string[]>([]);
  const [textFilter, setTextFilter] = useState<string>("");
  const [filterState, setFilterState] = useState<FilterRecordType>({});
  const [searchFlag, setSearchFlag] = useState(false);
  const [pageState, setPageState] = useState<PageState>({ page: 0, nbPages: 0, pageSize: 25 });
  const setSelectedPage = (page: number) => setPageState((prevState) => ({ ...prevState, page }));
  const setNbPages = (nbPages: number) => setPageState((prevState) => ({ ...prevState, nbPages }));
  const [searchState, setSearchState] = useState<SearchState>({ nbHits: 0, hits: [], page: 0 });


  const addFilter = (filterName: string, value: FilterSetting, filterType = "text" as FilterType) => {
    const valueTransformers = {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      array: (_prevState: FilterRecordType) => [...value as string[]] as ArrayFilterSetting,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      range: (_prevState: FilterRecordType) => (value as RangeFilterSetting),
      rangeMax: (prevState: FilterRecordType) => ({
        ...(prevState[filterName]?.value || {}) as RangeFilterSetting,
        max: value,
      }) as RangeFilterSetting,
      rangeMin: (prevState: FilterRecordType) => ({
        ...(prevState[filterName]?.value || {}) as RangeFilterSetting,
        min: value,
      } as RangeFilterSetting),
      boolean: (_prevState: FilterRecordType) => value,
      inverseBoolean: (_prevState: FilterRecordType) => value,
      text: (_prevState: FilterRecordType) => `${value}`,
      number: (_prevState: FilterRecordType) => value,
    } as Record<FilterType, (prevState: FilterRecordType) => FilterSetting>;
    const defaultTransform = () => `${value}`;
    const getNewValue = (prevState: FilterRecordType) =>
      (valueTransformers[filterType] || defaultTransform)(prevState);
    const getFilterType = () => {
      if (filterType === "rangeMin" || filterType === "rangeMax") {
        return "range";
      }
      if ((value === true || value === false) && filterType !== "inverseBoolean") {
        return "boolean";
      }
      return filterType;
    };
    setFilterState((prevState) => ({
      ...prevState,
      [filterName]: {
        type: getFilterType(),
        value: getNewValue(prevState),
      } as FilterData,
    }));
  };

  const removeFilter = (filterName: string, filterType?: FilterType) =>
    setFilterState((prevState) => {
      const newState = { ...prevState };
      if (["rangeMin", "rangeMax"].includes(filterType || "")) {
        const filterData = newState[filterName];
        if (!filterData) {
          return newState;
        }
        if (filterType === "rangeMin") {
          delete filterData.min;
        } else {
          delete filterData.max;
        }
        return { ...newState, [filterName]: filterData };
      }
      delete newState[filterName];
      return newState;
    });
  const setFilterCondition = useCallback((condition?: FilterCondition, resetPage = true) => {
    updateFilterCondition(condition);
    if (!resetPage) return;
    setPageState((previous) => ({ ...previous, page: 0 }));
    const { pathname } = window.location;
    const next = listPagePath(pathname);
    if (next && next !== pathname) navigate(next);
  }, [navigate]);
  const clearFilters = () => {
    setFilterState({});
    updateFilterCondition((previous) => (previous ? FilterCondition.AND : previous));
  };


  return {
    filterCondition,
    setFilterCondition,
    interestListIds,
    setInterestListIds,
    textFilter,
    searchFlag,
    setSearchFlag,
    setTextFilter,
    addFilter,
    removeFilter,
    clearFilters,
    filterState,
    setFilterState,
    search: () => setSearchFlag(true),
    searchState,
    setSearchState,
    pageState,
    setSelectedPage,
    setNbPages,
  };
};

export const useSidebarFilters = (): FilterManager => {
  const context = React.useContext(SidebarFiltersContext);
  if (typeof context === "undefined") {
    throw new Error("useSidebarFilters must be used within a SidebarFiltersProvider");
  }
  if (context === null) {
    throw new Error("useSidebarFilters is not initialized correctly");
  }
  return context;
};

export const SidebarFiltersProvider: React.FC<PropsWithChildren> = (props) => {
  const value = defineFilters();

  return (
    <SidebarFiltersContext.Provider value={value}>
      {props.children}
    </SidebarFiltersContext.Provider>
  );
};
